import { z } from 'zod';
import { cents, configKey, editableText, entityId, isoDateTime, publishable } from './primitives.js';

/**
 * MATCH IMOBILIARIO (Cap. 12)
 *
 * PROCURA + IMOVEL = MATCH. A pontuacao e configurada, nao codificada: o
 * administrador decide se localizacao vale mais que preco, e quanto de
 * tolerancia cabe antes de considerar compativel.
 */

/** A procura cadastrada pelo comprador (Cap. 12.2). */
export const buyerIntent = z.object({
  id: entityId,
  userId: entityId.nullable().default(null),
  /** Frase original, quando veio do "QUAL IMOVEL DESEJA?". */
  rawText: z.string().nullable().default(null),
  name: z.string().nullable().default(null),
  states: z.array(z.string()).default([]),
  cities: z.array(z.string()).default([]),
  neighborhoods: z.array(z.string()).default([]),
  regions: z.array(z.string()).default([]),
  minAreaM2: z.number().nullable().default(null),
  maxAreaM2: z.number().nullable().default(null),
  minPriceCents: cents.nullable().default(null),
  maxPriceCents: cents.nullable().default(null),
  topography: z.array(z.string()).default([]),
  documentation: z.array(z.string()).default([]),
  aptitudes: z.array(z.string()).default([]),
  infrastructure: z.array(z.string()).default([]),
  /** Criterios de campos dinamicos. */
  attributes: z.record(z.string(), z.unknown()).default({}),
  notes: z.string().nullable().default(null),
  /** Frequencia do alerta ao comprador. */
  alertFrequency: z.enum(['instant', 'daily', 'weekly', 'off']).default('instant'),
  active: z.boolean().default(true),
  createdAt: isoDateTime,
  expiresAt: isoDateTime.nullable().default(null),
});

export type BuyerIntent = z.infer<typeof buyerIntent>;

/**
 * Como pontuar a compatibilidade. Cada criterio tem peso e tolerancia; a
 * soma normalizada de 0 a 100 e comparada com `minimumScore`.
 */
export const matchCriterion = z.object({
  key: configKey,
  /** Campo do anuncio (aceita `attributes.x`). */
  listingField: z.string().min(1),
  /** Campo correspondente na procura. */
  intentField: z.string().min(1),
  strategy: z.enum(['exact', 'any_of', 'all_of', 'numeric_range', 'geo_distance', 'text_similarity']),
  weight: z.number().min(0).max(10).default(1),
  /**
   * Tolerancia. Em `numeric_range`, percentual aceito fora da faixa (0.1 =
   * aceita 10% acima do preco maximo). Em `geo_distance`, quilometros.
   */
  tolerance: z.number().min(0).default(0),
  /** Se verdadeiro e o criterio nao bater, o match e descartado. */
  mandatory: z.boolean().default(false),
  publication: publishable.default({}),
});

export type MatchCriterion = z.infer<typeof matchCriterion>;

export const matchConfig = z.object({
  version: z.string(),
  criteria: z.array(matchCriterion).default([]),
  /** Score minimo (0-100) para registrar o match. */
  minimumScore: z.number().min(0).max(100).default(60),
  /** Score a partir do qual a retaguarda DIREX e acionada (Cap. 12.4). */
  backofficeScore: z.number().min(0).max(100).default(75),
  /** Nao notifica o mesmo par comprador+imovel de novo antes disso. */
  dedupeWindowDays: z.number().int().min(0).default(30),
  /** Teto de matches enviados por procura por dia. */
  maxMatchesPerIntentPerDay: z.number().int().min(1).default(10),
  /** Gera tarefa comercial automaticamente (Cap. 12.3, item 6). */
  createCrmTask: z.boolean().default(true),
  crmPipelineKey: configKey.nullable().default(null),
  crmStageKey: configKey.nullable().default(null),
  notificationTemplateKey: configKey.nullable().default(null),
});

export type MatchConfig = z.infer<typeof matchConfig>;

export const matchStatus = z.enum(['new', 'notified', 'in_service', 'converted', 'discarded', 'expired']);

export const matchRecord = z.object({
  id: entityId,
  intentId: entityId,
  listingId: entityId,
  listingCode: z.string(),
  score: z.number().min(0).max(100),
  /** Quais criterios bateram e quanto cada um contribuiu — auditavel. */
  breakdown: z
    .array(z.object({ criterion: z.string(), matched: z.boolean(), contribution: z.number(), detail: z.string().nullable().default(null) }))
    .default([]),
  status: matchStatus.default('new'),
  assignedTo: entityId.nullable().default(null),
  leadId: entityId.nullable().default(null),
  createdAt: isoDateTime,
  notifiedAt: isoDateTime.nullable().default(null),
});

export type MatchRecord = z.infer<typeof matchRecord>;

/** Linha da retaguarda DIREX (Cap. 12.4). Campos sensiveis so para perfis internos. */
export const matchBackofficeRow = matchRecord.extend({
  buyerName: z.string().nullable().default(null),
  buyerPhone: z.string().nullable().default(null),
  buyerWhatsapp: z.string().nullable().default(null),
  criteriaSummary: editableText.default(''),
  listingTitle: z.string(),
  advertiserName: z.string().nullable().default(null),
  serviceStatus: z.string().nullable().default(null),
});

export type MatchBackofficeRow = z.infer<typeof matchBackofficeRow>;
